import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { useTranslation } from '~/hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import { useBenefits } from '../../coach_services/BenefitsProvider'
import ScheduleDialog from '../../coach_services/ProfessionalServices/ScheduleDialog'
import FeedbackDialog from '../../coach_services/ProfessionalServices/FeedbackDialog'

export function PremiumCoachCard({ className }) {
  const [isLoading, setIsLoading] = useState(false)
  const [coachInfo, setCoachInfo] = useState(null)
  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/coach_info')
      .then((response) => {
        if (response.status == 200) {
          setCoachInfo(response.data)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setCoachInfo])

  return isLoading || _.isEmpty(coachInfo) ? (
    <div className="flex items-center justify-center">
      <Spinner show size={9} />
    </div>
  ) : (
    <>
      {coachInfo?.liaison ? (
        <LiaisonCard className={className} />
      ) : (
        <DedicatedCoachCard className={className} coach={coachInfo} />
      )}
    </>
  )
}

function LiaisonCard({ className }) {
  const { t } = useTranslation()
  return (
    <Card
      className={className}
      actions={
        <div className="grid w-full grid-cols-1 gap-4 @md:grid-cols-2">
          <Button
            color="secondary"
            href="/coach_services/professional_services"
          >
            {t('premium_liason_coach_card.professional_services')}
          </Button>
          <Button
            color="secondary"
            variant="outline"
            href="/coach_services/meet_your_coach"
          >
            {t('premium_liason_coach_card.view_action')}
          </Button>
        </div>
      }
    >
      <Stack className="h-full justify-between" spacing={3}>
        <div className="flex items-end gap-4">
          <img
            className="mt-[-45px] h-48 w-48 rounded-full"
            src="https://www-yns-com.s3.us-east-2.amazonaws.com/images/coach_services/liason_team.png"
            alt="Coach Image"
          />
          <Stack spacing={3}>
            <Typography variant="title4" color="secondary">
              {t('premium_liason_coach_card.name')}
            </Typography>
            <Typography>
              {t('premium_liason_coach_card.description')}
            </Typography>
          </Stack>
        </div>
      </Stack>
    </Card>
  )
}

function DedicatedCoachCard({ className, coach }) {
  const { t } = useTranslation()
  const { current_user, serviceRequestList } = useBenefits()
  const [coachState, setCoachState] = useState('ORIENTATION_CALL_NOT_SCHEDULED')
  const [orientationCall, setOrientationCall] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [resumeReviewCall, setResumeReviewCall] = useState(null)
  const [nextServiceRequest, setNextServiceRequest] = useState(null)
  const [feedbackRequest, setFeedbackRequest] = useState(null)

  useEffect(() => {
    const orientationCall =
      serviceRequestList['ServiceRequest::OrientationCall']
    setOrientationCall(orientationCall)
    const resumeReviewCall = serviceRequestList['ServiceRequest::ResumeReview']
    setResumeReviewCall(resumeReviewCall)
    // console.log('serviceRequestList', serviceRequestList)
    // console.log('orientationCall', orientationCall)
    // console.log('resumeReviewCall', resumeReviewCall)

    if (_.isEmpty(orientationCall) || !orientationCall.calendly_event) {
      setCoachState('ORIENTATION_CALL_NOT_SCHEDULED') // State 2
      return
    }

    if (orientationCall?.calendly_event && !orientationCall.completed) {
      setCoachState('ORIENTATION_CALL_SCHEDULED') // State 3
      return
    }

    const hasScheduledRequests = Object.entries(serviceRequestList).some(
      ([key, request]) =>
        key !== 'ServiceRequest::OrientationCall' &&
        request.calendly_event &&
        !request.completed,
    )
    // Find next service request
    const nextRequest = Object.entries(serviceRequestList)
      .filter(
        ([key, request]) =>
          key !== 'ServiceRequest::OrientationCall' && !request.completed,
      )
      .map(([_, request]) => request)
      .filter((request) => {
        if (request.calendly_event) {
          return moment(request.calendly_event.end_time).isAfter(moment())
        } else {
          return request.due_at && moment(request.due_at).isAfter(moment())
        }
      })
      .sort((a, b) => moment(a.due_at).diff(moment(b.due_at)))[0]
    setNextServiceRequest(nextRequest)

    if (
      !_.isEmpty(resumeReviewCall) &&
      JSON.stringify(nextRequest) === JSON.stringify(resumeReviewCall) &&
      !resumeReviewCall.meetingViaCalendly
    ) {
      setCoachState('RESUME_REVIEW_NO_MEETING') // State 4b
      return
    }

    if (hasScheduledRequests && nextRequest) {
      setCoachState('SERVICE_REQUEST_SCHEDULED') // State 4
    } else {
      // Find feedback request
      const feedback = Object.entries(serviceRequestList)
        .filter(
          ([key, request]) =>
            key !== 'ServiceRequest::OrientationCall' &&
            request.completed &&
            !request.viewed,
        )
        .map(([_, request]) => request)
        .sort((a, b) =>
          moment(b.calendly_event.start_time).diff(
            moment(a.calendly_event.start_time),
          ),
        )[0]
      setFeedbackRequest(feedback)

      if (feedback) {
        setCoachState('SERVICE_REQUEST_FEEDBACK') // State 5
      } else {
        const hasCompletedRequests = Object.entries(serviceRequestList).some(
          ([key, request]) =>
            key !== 'ServiceRequest::OrientationCall' && request.completed,
        )

        if (hasCompletedRequests) {
          setCoachState('SERVICE_REQUEST_COMPLETED') // State 6
        } else {
          setCoachState('SERVICE_REQUEST_COMPLETED') // Use this state for now
        }
      }
    }
  }, [serviceRequestList])

  const renderActions = () => {
    switch (coachState) {
      case 'ORIENTATION_CALL_SCHEDULED':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={orientationCall.calendly_event?.join_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_scheduled.join_meeting',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )
      case 'ORIENTATION_CALL_NOT_SCHEDULED':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="schedule-meeting"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_not_scheduled.schedule_meeting',
              )}
            </Button>
            <Button
              key="meet-your-coach"
              href="/coach_services/meet_your_coach"
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_not_scheduled.meet_your_coach',
              )}
            </Button>
          </div>
        )
      case 'SERVICE_REQUEST_SCHEDULED':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={nextServiceRequest?.calendly_event?.join_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_scheduled.join_meeting',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )

      case 'RESUME_REVIEW_NO_MEETING':
        return (
          <Button
            key="coach-services"
            href="/coach_services/professional_services"
            variant="outline"
            color="secondary"
          >
            {t('premium_dedicated_coach_card.coach_services')}
          </Button>
        )
      case 'SERVICE_REQUEST_FEEDBACK':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href="/coach_services/feedback"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_feedback.view_feedback',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )
      case 'SERVICE_REQUEST_COMPLETED':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.view_coach_services')}
            </Button>
            <Button
              key="message-your-coach"
              className="open-chat-modal w-full"
              data-toggle="modal"
              data-target="#coach_chat_list"
              data-action={t(
                'premium_dedicated_coach_card.service_request_completed.message_your_coach',
              )}
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_completed.message_your_coach',
              )}
            </Button>
          </div>
        )
    }
  }

  const renderDescription = () => {
    switch (coachState) {
      case 'ORIENTATION_CALL_SCHEDULED':
        return t(
          'premium_dedicated_coach_card.orientation_call_scheduled.description',
          {
            meeting_time: `${moment(
              orientationCall.calendly_event?.start_time,
            ).format(
              'MM/DD/YYYY',
            )} at ${moment(orientationCall.calendly_event?.start_time).format('h:mm A')}`,
          },
        )
      case 'ORIENTATION_CALL_NOT_SCHEDULED':
        return t(
          'premium_dedicated_coach_card.orientation_call_not_scheduled.description',
        )
      case 'RESUME_REVIEW_NO_MEETING': {
        // Calculate due_at as created_at plus 3 business days
        const createdAt = moment(resumeReviewCall.created_at)
        let dueAt = createdAt.clone()
        let addedDays = 0
        while (addedDays < 3) {
          dueAt = dueAt.add(1, 'days')
          // 0 = Sunday, 6 = Saturday
          if (dueAt.day() !== 0 && dueAt.day() !== 6) {
            addedDays++
          }
        }
        return t(
          'premium_dedicated_coach_card.resume_review_no_meeting.description',
          {
            due_at: dueAt.format('MM/DD/YYYY'),
          },
        )
      }
      case 'SERVICE_REQUEST_SCHEDULED':
        return t(
          'premium_dedicated_coach_card.service_request_scheduled.description',
          {
            service_name: nextServiceRequest?.name,
            meeting_time: `${moment(
              nextServiceRequest?.calendly_event?.start_time,
            ).format(
              'MM/DD/YYYY',
            )} at ${moment(nextServiceRequest?.calendly_event?.start_time).format('h:mm A')}`,
          },
        )
      case 'SERVICE_REQUEST_FEEDBACK':
        return t(
          'premium_dedicated_coach_card.service_request_feedback.description',
          {
            service_name: feedbackRequest?.calendly_event?.name,
          },
        )
      case 'SERVICE_REQUEST_COMPLETED':
        return t(
          'premium_dedicated_coach_card.service_request_completed.description',
        )
    }
  }

  return (
    <Card className={className} actions={renderActions()}>
      <Stack className="h-full justify-between" spacing={3}>
        <div className="flex flex-col items-end gap-4 @md:flex-row">
          {coach?.headshot ? (
            <img
              className="mt-[-80px] h-60 w-60 object-contain"
              src={coach?.headshot}
              alt="Coach headshot"
            />
          ) : (
            <img
              className="mt-[-60px] h-48 w-48 rounded-full"
              src={coach?.image}
              alt="Coach Image"
            />
          )}
          <Stack spacing={3}>
            <Typography variant="title4" color="secondary">
              {coach.name}
            </Typography>
            {renderDescription()}
          </Stack>
        </div>
      </Stack>
      <ScheduleDialog current_user={current_user} coach={coach} />
      <FeedbackDialog />
    </Card>
  )
}
